import { PitchDetector } from "pitchy";
import * as Tone from "tone";

/**
 * Professional Audio Engine
 * Handles AudioContext lifecycle, analyser node setup, and pitch detection logic.
 * Implements Singleton pattern.
 */
class AudioEngine {
  constructor() {
    this.audioContext = null;
    this.analyser = null;
    this.pitchDetector = null;
    this.audioBuffer = null;
    this.sourceNode = null;
    this.player = null;
    this.gainNode = null;

    // Effects
    this.harmony3Shift = null;
    this.harmony3Gain = null;
    this.harmony5Shift = null;
    this.harmony5Gain = null;
    this.micGain = null;

    this.isRunning = false;
    this.mode = 'mic';

    this.currentTrackName = "No Track Loaded";
  }

  static getInstance() {
    if (!AudioEngine.instance) {
      AudioEngine.instance = new AudioEngine();
    }
    return AudioEngine.instance;
  }

  async init() {
    if (this.audioContext) return;

    await Tone.start();
    this.audioContext = Tone.getContext().rawContext;

    // --- Master Chain ---
    this.gainNode = new Tone.Gain(0.8).toDestination();

    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 2048;
    this.gainNode.connect(this.analyser);

    this.audioBuffer = new Float32Array(this.analyser.fftSize);
    this.pitchDetector = PitchDetector.forFloat32Array(this.analyser.fftSize);

    // --- Processing Chain ---
    this.micGain = new Tone.Gain(1.0).connect(this.gainNode);

    // Harmonies
    this.harmony3Shift = new Tone.PitchShift({ pitch: 4, windowSize: 0.1, delayTime: 0, feedback: 0 });
    this.harmony3Gain = new Tone.Gain(0.0).connect(this.gainNode);
    this.harmony3Shift.connect(this.harmony3Gain);

    this.harmony5Shift = new Tone.PitchShift({ pitch: 7, windowSize: 0.1, delayTime: 0, feedback: 0 });
    this.harmony5Gain = new Tone.Gain(0.0).connect(this.gainNode);
    this.harmony5Shift.connect(this.harmony5Gain);

    // Initialize Player synced to Transport
    this.player = new Tone.Player().toDestination();
    this.player.connect(this.gainNode);
    this.player.sync().start(0);
  }

  async loadTrack(fileOrUrl, name) {
    if (!this.player) await this.init();

    Tone.Transport.stop();
    const url = typeof fileOrUrl === 'string' ? fileOrUrl : URL.createObjectURL(fileOrUrl);
    await this.player.load(url);

    this.currentTrackName = name || (fileOrUrl.name ? fileOrUrl.name : "Unknown Track");
    console.log("Track loaded:", this.currentTrackName);
    this.mode = 'player';
  }

  async startMic() {
    await this.init();
    this.stop();
    this.currentTrackName = "Microphone Input";

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: false, autoGainControl: false, noiseSuppression: false }
    });
    const micSource = new Tone.UserMedia();
    await micSource.open();

    micSource.connect(this.micGain);
    micSource.connect(this.harmony3Shift);
    micSource.connect(this.harmony5Shift);

    this.mode = 'mic';
    this.isRunning = true;
  }

  play() {
    if (!this.player) return;

    if (this.mode === 'player' && this.player.loaded) {
      if (Tone.Transport.state !== 'started') {
        Tone.Transport.start();
      }
      this.isRunning = true;
    } else if (this.mode === 'mic') {
      if (Tone.context.state === 'suspended') Tone.context.resume();
      this.isRunning = true;
    }
  }

  stop() {
    if (this.mode === 'player') {
      Tone.Transport.stop(); // Stop AND Reset to 0
      // If pause is desired: Tone.Transport.pause();
    }
    this.isRunning = false;
  }

  setVolume(val) {
    if (this.gainNode) this.gainNode.gain.rampTo(val, 0.1);
  }

  setHarmonyLevel(type, val) {
    if (!this.harmony3Gain) return;
    if (type === 3) this.harmony3Gain.gain.rampTo(val, 0.1);
    if (type === 5) this.harmony5Gain.gain.rampTo(val, 0.1);
  }

  seek(progress) {
    if (this.player && this.player.loaded) {
      const duration = this.player.buffer.duration;
      Tone.Transport.seconds = duration * progress;
    }
  }

  getProgress() {
    if (this.mode === 'player' && this.player && this.player.loaded) {
      const duration = this.player.buffer.duration;
      if (duration === 0) return 0;
      return Tone.Transport.seconds / duration;
    }
    return 0;
  }

  getAnalysis() {
    if (!this.analyser) return { frequency: 0, clarity: 0 };

    this.analyser.getFloatTimeDomainData(this.audioBuffer);
    const [frequency, clarity] = this.pitchDetector.findPitch(
      this.audioBuffer,
      this.audioContext.sampleRate
    );
    return { frequency, clarity };
  }
}

export const audioEngine = AudioEngine.getInstance();